import type {AuthProvider} from "@refinedev/core"
import i18n from "../../i18n"
import {store} from "../../features/store"
import {authByLoginAndPasswordAction} from "../../features/auth/authByLoginAndPasswordAction"
import {logout} from "../../features/auth/authSlice"
import {resetSessionData} from "../../features/session/resetSessionData"
import {translateUi} from "../../i18n/translateUi"

interface LoginParams {
    username?: string
    password?: string
}

const getMessageFromError = (error: unknown, fallback = i18n.t("auth.loginError")) => {
    if (typeof error === "string") return error

    if (error && typeof error === "object") {
        if ("message" in error && typeof error.message === "string") {
            return error.message
        }

        if ("data" in error && error.data && typeof error.data === "object") {
            const message = (error.data as {message?: unknown}).message
            if (typeof message === "string") return message
        }
    }

    return fallback
}

export const authProvider: AuthProvider = {
    login: async (params: LoginParams) => {
        if (!params?.username || !params?.password) {
            return {
                success: false,
                error: new Error(i18n.t("auth.fillCredentials"))
            }
        }

        const action = await store.dispatch(
            authByLoginAndPasswordAction({
                username: params.username,
                password: params.password
            })
        )

        if (authByLoginAndPasswordAction.fulfilled.match(action)) {
            resetSessionData()
            return {
                success: true,
                redirectTo: "/"
            }
        }

        return {
            success: false,
            error: new Error(getMessageFromError(action.payload || action.error))
        }
    },
    logout: async () => {
        store.dispatch(logout())
        resetSessionData()

        return {
            success: true,
            redirectTo: "/login"
        }
    },
    check: async () => {
        const {token, expiresAt} = store.getState().auth
        const isExpired = Boolean(expiresAt && Date.now() > Number(expiresAt))

        if (token && !isExpired) {
            return {
                authenticated: true
            }
        }

        if (token && isExpired) {
            store.dispatch(logout())
            resetSessionData()
        }

        return {
            authenticated: false,
            redirectTo: "/login"
        }
    },
    onError: async (error) => {
        const statusCode = error?.statusCode || error?.status

        if (statusCode === 401) {
            store.dispatch(logout())
            resetSessionData()

            return {
                logout: true,
                redirectTo: "/login",
                error
            }
        }

        return {
            error
        }
    },
    getPermissions: async () => [],
    getIdentity: async () => ({
        name: translateUi("Администратор")
    })
}
